import React from 'react';
import { Card, Tag, Avatar, Space, Typography, Badge } from 'antd';
import { UserOutlined } from '@ant-design/icons';

const { Text } = Typography;

export interface MobileCardField<T> {
  key: keyof T | string;
  label?: string;
  render?: (value: any, record: T) => React.ReactNode;
  hidden?: boolean;
}

export interface StatusConfig {
  color: string;
  text?: string;
}

export interface AvatarConfig<T> {
  field?: keyof T;
  icon?: React.ReactNode;
  shape?: 'circle' | 'square';
}

export interface MobileDataCardProps<T extends Record<string, any>> {
  data: T;
  primaryField: keyof T;
  secondaryField?: keyof T;
  statusField?: keyof T;
  statusConfig?: Record<string, StatusConfig>;
  fields?: MobileCardField<T>[];
  avatar?: AvatarConfig<T>;
  onClick?: (data: T) => void;
  extra?: React.ReactNode;
  badge?: {
    field: keyof T;
    color?: string;
  };
  className?: string;
  style?: React.CSSProperties;
}

function MobileDataCard<T extends Record<string, any>>({
  data,
  primaryField,
  secondaryField,
  statusField,
  statusConfig = {},
  fields = [],
  avatar,
  onClick,
  extra,
  badge,
  className,
  style,
}: MobileDataCardProps<T>) {
  const primaryValue = data[primaryField];
  const secondaryValue = secondaryField ? data[secondaryField] : null;
  const statusValue = statusField ? data[statusField] : null;

  const getStatusConfig = (status: string): StatusConfig => {
    const normalizedStatus = status?.toString().toLowerCase();

    // Default status colors
    const defaultConfig: Record<string, StatusConfig> = {
      active: { color: 'green', text: 'Active' },
      inactive: { color: 'default', text: 'Inactive' },
      pending: { color: 'orange', text: 'Pending' },
      completed: { color: 'green', text: 'Completed' },
      failed: { color: 'red', text: 'Failed' },
      approved: { color: 'blue', text: 'Approved' },
      rejected: { color: 'red', text: 'Rejected' },
      new: { color: 'blue', text: 'New' },
      qualified: { color: 'cyan', text: 'Qualified' },
      converted: { color: 'green', text: 'Converted' },
      lost: { color: 'default', text: 'Lost' },
      high: { color: 'red', text: 'High' },
      medium: { color: 'orange', text: 'Medium' },
      low: { color: 'green', text: 'Low' },
    };

    return statusConfig[normalizedStatus] || defaultConfig[normalizedStatus] || { color: 'default', text: status };
  };

  const renderFieldValue = (field: MobileCardField<T>) => {
    const value = typeof field.key === 'string' && field.key.includes('.')
      ? field.key.split('.').reduce((obj, key) => obj?.[key], data as any)
      : data[field.key as keyof T];

    if (field.render) {
      return field.render(value, data);
    }

    if (value === null || value === undefined) {
      return <Text type="secondary">-</Text>;
    }

    // Handle date values
    if (value instanceof Date || (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value))) {
      return new Date(value).toLocaleDateString();
    }

    // Handle numbers
    if (typeof value === 'number') {
      return value.toLocaleString();
    }

    return String(value);
  };

  const cardContent = (
    <Card
      className={`mobile-data-card ${className || ''}`}
      style={{
        marginBottom: 12,
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        ...style,
      }}
      bodyStyle={{ padding: 16 }}
      hoverable={!!onClick}
      onClick={() => onClick?.(data)}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        {/* Avatar */}
        {avatar && (
          <Avatar
            size={48}
            icon={avatar.icon || <UserOutlined />}
            src={avatar.field ? data[avatar.field] : undefined}
            shape={avatar.shape || 'circle'}
            style={{ flexShrink: 0 }}
          />
        )}

        {/* Main content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Header row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text
                strong
                style={{
                  fontSize: 16,
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {primaryValue}
              </Text>
              {secondaryValue && (
                <Text
                  type="secondary"
                  style={{
                    fontSize: 13,
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {secondaryValue}
                </Text>
              )}
            </div>

            {/* Status tag */}
            {statusValue && (
              <Tag color={getStatusConfig(statusValue).color} style={{ marginLeft: 8, flexShrink: 0 }}>
                {getStatusConfig(statusValue).text || statusValue}
              </Tag>
            )}
          </div>

          {/* Additional fields */}
          {fields.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', marginTop: 8 }}>
              {fields.filter(f => !f.hidden).map((field, index) => (
                <div key={index} style={{ minWidth: '40%', flex: '1 1 40%' }}>
                  {field.label && (
                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                      {field.label}
                    </Text>
                  )}
                  <Text style={{ fontSize: 13 }}>
                    {renderFieldValue(field)}
                  </Text>
                </div>
              ))}
            </div>
          )}

          {/* Extra content */}
          {extra && (
            <div style={{ marginTop: 12 }}>
              {extra}
            </div>
          )}
        </div>
      </div>
    </Card>
  );

  // Wrap with badge if configured
  if (badge && data[badge.field]) {
    return (
      <Badge.Ribbon text={data[badge.field]} color={badge.color || 'blue'}>
        {cardContent}
      </Badge.Ribbon>
    );
  }

  return cardContent;
}

export default MobileDataCard;
